import { createContext, useContext, useState, ReactNode, useCallback, useMemo, useEffect } from "react";
import { classifySku, type SkuClassification } from "@/data/inventory/skuValidation";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import { WarehouseService } from "@/services/warehouse.service";
import { WarehousePageService } from "@/services/warehouse-page.service";
import { getProducts } from "@/services/productRegistry.service";
import { syncJobMaterialState } from "@/services/inventory.service";
import type { RegistryProduct } from "@/data/ProductRegistryContext";
import { type ProcurementAuditEvent } from "@/data/VendorPOContext";

/* ═══════════════════════════════════════════════════
   Warehouse Inventory Control System
   ──────────────────────────────────────────────────
   ✅ CANONICAL INVENTORY OWNER (Phase 1, 2026-04-28)
   Source of truth for physical stock during beta.
   See: src/data/inventory/CANONICAL.md

   SKU-level tracking: incoming, on-hand, reserved, allocated.
   Note: SKU namespace unification with JobSpec BOM is a later phase;
   for now, mismatched SKUs from upstream BOMs/POs are advisory.
   ═══════════════════════════════════════════════════ */

export type ReceivingStatus = "Pending" | "Partially Received" | "Fully Received";
export type AllocationStatus = "Unallocated" | "Reserved" | "Ready for Install" | "Installed";
export type DiscrepancyType = "Missing" | "Damaged" | "Wrong Item" | "Quantity Mismatch";
export type ShipmentStatus = "Ordered" | "In Transit" | "Delivered" | "Received";

export interface StockRecord {
  sku: string;
  productName: string;
  currentStock: number;
  reservedStock: number;
  allocatedStock: number;
  incomingStock: number;
  availableStock: number;
  reorderThreshold: number;
  hasReceived?: boolean;
}

export interface ReceivingRecord {
  allocationBlocked: boolean;
  auditEvents: ProcurementAuditEvent[];
  id: string;
  poId: string;
  poNumber: string;
  vendorName: string;
  jobId?: string | null;
  jobLabel?: string | null;
  sku: string;
  productName: string;
  expectedQuantity: number;
  receivedQuantity: number;
  status: ReceivingStatus;
  receivedAt?: string;
  receivedBy?: string;
  shipmentId?: string;
  discrepancy?: {
    type: DiscrepancyType;
    notes: string;
  };
  /** Phase 3: SKU classification stamped at receive time (audit). */
  skuClassification?: SkuClassification;
  /** Phase 3: non-blocking warning surfaced when an unmapped SKU is received. */
  unmappedWarning?: string;
}

export interface JobAllocation {
  id: string;
  jobId: string;
  jobLabel: string;
  sku: string;
  productName: string;
  quantity: number;
  status: AllocationStatus;
  allocatedAt?: string;
  allocatedBy?: string;
  customerName?: string;
  projectType?: string;
  /** Phase 4A: non-blocking audit field; populated when reservation
   *  exceeds available stock at the time of allocation. */
  overAvailableWarning?: string;
}

/* ── Shipment: vendor-level grouping of PO items ── */
export interface ShipmentItem {
  sku: string;
  productName: string;
  expectedQuantity: number;
  receivedQuantity: number;
  jobId?: string | null;
  jobLabel?: string | null;
}

export interface Shipment {
  id: string;
  poId: string;
  poNumber: string;
  externalPoNumber?: string;
  vendorId: string | null;
  vendorName: string;
  items: ShipmentItem[];
  status: ShipmentStatus;
  createdAt: string;
  expectedDelivery?: string;
  deliveredAt?: string;
  receivedAt?: string;
  warehouseId?: string | null;
  warehouseName?: string | null;
  movementStage?: string | null;
}

export interface WarehouseInventoryContextValue {
  stockRecords: StockRecord[];
  receivingRecords: ReceivingRecord[];
  jobAllocations: JobAllocation[];
  shipments: Shipment[];
  setReceivingRecords: React.Dispatch<React.SetStateAction<ReceivingRecord[]>>;
  setJobAllocations: React.Dispatch<React.SetStateAction<JobAllocation[]>>;
  setShipments: React.Dispatch<React.SetStateAction<Shipment[]>>;
  confirmReceiving: (recordId: string, quantityReceived: number, discrepancy?: { type: DiscrepancyType; notes: string }) => void;
  allocateToJob: (sku: string, jobId: string, jobLabel: string, quantity: number, productName?: string) => Promise<JobAllocation | null>;
  markReadyForInstall: (allocationId: string) => Promise<void>;
  markAsInstalled: (allocationId: string) => Promise<void>;
  getStockBySku: (sku: string) => StockRecord | undefined;
  getPendingReceiving: () => ReceivingRecord[];
  getAllocationsByJob: (jobId: string) => JobAllocation[];
  lowStockItems: StockRecord[];
  /** Create shipment + receiving records from a submitted PO */
  createShipmentsFromPO: (po: {
    poId: string; poNumber: string; vendorId: string; vendorName: string;
    jobId: string; jobLabel: string;
    items: { sku: string; productName: string; quantity: number }[];
  }) => Promise<void>;
  /** Update a shipment status */
  updateShipmentStatus: (shipmentId: string, status: ShipmentStatus) => void;
  /** Receive an entire shipment */
  receiveShipment: (shipmentId: string, itemReceipts: { sku: string; received: number; discrepancy?: { type: DiscrepancyType; notes: string } }[]) => void;
  /** Check if a job has all materials ready for install */
  isJobReadyForInstall: (jobId: string) => { ready: boolean; reasons: string[] };
  /** Re-fetch latest warehouse records from DB */
  refreshData: () => Promise<void>;
}

/**
 * STOCK_SEED — Phase 3 (Causal Receiving → Inventory Stock, 2026-04-28)
 * ────────────────────────────────────────────────────────────────────
 * SKU catalog for product name lookup. Used by guardrail tests and SKU
 * classification UI badges via getCatalogSkus() and getCatalogProductName().
 */
const STOCK_SEED: Record<string, { name: string; stock: number; reorder: number }> = {
  // ── Deprecated (pre-JobSpec demo seed) ──────────────────────────────
  "WALL-SEN-STD": { name: "Sentrel Wall Panel – Standard", stock: 24, reorder: 6 },
  "WALL-DEK-STD": { name: "Dekton Wall Panel – Standard", stock: 8, reorder: 3 },
  "WALL-TIL-STD": { name: "Tile Wall – Standard", stock: 12, reorder: 4 },
  "BASE-SEN-3060": { name: "Sentrel Safety Base 30×60", stock: 18, reorder: 5 },
  "BASE-DEK-CUST": { name: "Dekton Custom Base", stock: 0, reorder: 0 },
  "GLS-CLR-STD": { name: "Clear Glass Panel – Standard", stock: 10, reorder: 3 },
  "GLS-SLD-KIT": { name: "Sliding Glass Door Kit", stock: 6, reorder: 2 },
  "FIX-STD-CHR": { name: "Chrome Fixture Kit", stock: 30, reorder: 10 },
  "ACC-GRAB-12": { name: '12" Grab Bar – Chrome', stock: 50, reorder: 15 },
  "ACC-GRAB-18": { name: '18" Grab Bar – Chrome', stock: 40, reorder: 12 },
  "ACC-GRAB-24": { name: '24" Grab Bar – Chrome', stock: 35, reorder: 10 },
  "ACC-SEAT-FLD": { name: "Fold-Down Bench Seat", stock: 15, reorder: 5 },
  "ACC-CADDY-SS": { name: "Corner Caddy – Stainless", stock: 40, reorder: 10 },
  "DRN-LIN-STD": { name: "Drain Kit – Linear", stock: 20, reorder: 8 },
  "WP-MEM-KIT": { name: "Waterproofing Membrane Kit", stock: 15, reorder: 5 },

  // ── Canonical JobSpec BOM (TTS-STD-60x32 v1) ─────────────────────────
  "PAN-60x32-WHT": { name: "Shower Pan 60×32 White", stock: 0, reorder: 0 },
  "WALL-KIT-3PC-60x32": { name: "3-Piece Wall Surround 60×32", stock: 0, reorder: 0 },
  "VALVE-PB-1H": { name: "Pressure-Balance Mixing Valve, 1-Handle", stock: 0, reorder: 0 },
  "TRIM-CHR-RND": { name: "Valve Trim — Chrome, Round", stock: 0, reorder: 0 },
  "SHWR-HEAD-CHR": { name: "Shower Head — Chrome", stock: 0, reorder: 0 },
  "DRN-ABS-2IN": { name: 'Shower Drain Assembly 2"', stock: 0, reorder: 0 },
  "DOOR-GL-60-CHR": { name: 'Sliding Glass Door 60" — Chrome', stock: 0, reorder: 0 },
  "NICHE-12x24": { name: "Recessed Niche 12×24", stock: 0, reorder: 0 },
  "CAULK-100SIL-WHT": { name: "100% Silicone Caulk — White", stock: 0, reorder: 0 },
  "CEMBOARD-3x5": { name: 'Cement Backer Board 3×5×½"', stock: 0, reorder: 0 },
  "SCREW-CB-1.25": { name: 'Cement Board Screws 1¼"', stock: 0, reorder: 0 },
  "MEMBRANE-WP": { name: "Waterproof Membrane Roll", stock: 0, reorder: 0 },
  // ── Canonical JobSpec BOM (WIT-STD-52x30 v1) ─────────────────────────
  "WIT-UNIT-STD": { name: "Walk-in Tub Unit, Standard", stock: 0, reorder: 0 },
  "WIT-SURROUND-3PC": { name: "Walk-in Tub Surround, 3-Piece", stock: 0, reorder: 0 },
  "TUB-FILL-CHR": { name: "Tub Filler — Chrome", stock: 0, reorder: 0 },
  "DRN-OVF-2IN": { name: "Drain/Overflow Kit 2in", stock: 0, reorder: 0 },
  "GRAB-BAR-24-CHR": { name: "Grab Bar 24in — Chrome", stock: 0, reorder: 0 },
  "VANITY-36-WHT": { name: "Vanity 36in — White", stock: 0, reorder: 0 },
  "VAN-FAUCET-CHR": { name: "Vanity Faucet — Chrome", stock: 0, reorder: 0 },
  "TOILET-RH-WHT": { name: "Toilet RH — White", stock: 0, reorder: 0 },
  "WAX-RING-STD": { name: "Wax Ring — Standard", stock: 0, reorder: 0 },
  "MIRROR-30x36": { name: "Mirror 30x36", stock: 0, reorder: 0 },
  // ── Canonical JobSpec BOM (WIN-DH-36x60 v1) ─────────────────────────
  "WIN-DH-36x60-WHT": { name: "Window Double-Hung 36x60 — White", stock: 0, reorder: 0 },
  "WIN-SCREEN-36x60": { name: "Window Screen 36x60", stock: 0, reorder: 0 },
  "FLASH-TAPE-4IN": { name: "Flashing Tape 4in", stock: 0, reorder: 0 },
  "FOAM-WIN-DOOR": { name: "Window/Door Foam", stock: 0, reorder: 0 },
  "SHIM-CDR-PK": { name: "Cedar Shims Pack", stock: 0, reorder: 0 },
  "TRIM-CASING-PVC": { name: "PVC Casing Trim", stock: 0, reorder: 0 },
  "SCREW-EXT-2.5": { name: "Exterior Screws 2.5in", stock: 0, reorder: 0 },
};

/**
 * Read-only accessor for the canonical catalog SKU set.
 * Used by guardrail tests and SKU classification UI badges.
 * Do NOT mutate the returned set.
 */
export function getCatalogSkus(): ReadonlySet<string> {
  return new Set(Object.keys(STOCK_SEED));
}

export function getCatalogProductName(sku: string): string | undefined {
  return STOCK_SEED[sku]?.name;
}

const WarehouseInventoryContext = createContext<WarehouseInventoryContextValue | null>(null);

async function loadWarehouseSnapshot() {
  const [shipmentsResult, receivingResult, allocationsResult, productsResult, jobMaterialsResult] =
    await Promise.allSettled([
      WarehouseService.fetchShipments(),
      WarehouseService.fetchReceivingRecords(),
      WarehouseService.fetchJobAllocations(),
      getProducts(),
      WarehouseService.fetchAllJobMaterialRequirements(),
    ]);

  if (shipmentsResult.status === "rejected") {
    console.error("[WarehouseInventory] Failed to load shipments:", shipmentsResult.reason);
  }
  if (receivingResult.status === "rejected") {
    console.error("[WarehouseInventory] Failed to load receiving records:", receivingResult.reason);
  }
  if (allocationsResult.status === "rejected") {
    console.error("[WarehouseInventory] Failed to load job allocations:", allocationsResult.reason);
  }
  if (productsResult.status === "rejected") {
    console.error("[WarehouseInventory] Failed to load product registry:", productsResult.reason);
  }
  if (jobMaterialsResult.status === "rejected") {
    console.error("[WarehouseInventory] Failed to load job_materials requirements:", jobMaterialsResult.reason);
  }

  // Build Map<jobId, Map<sku, quantityRequired>> for fast readiness lookups
  const rawJobMaterials = jobMaterialsResult.status === "fulfilled" ? jobMaterialsResult.value : [];
  const jobMaterialsByJobId = new Map<string, Map<string, number>>();
  for (const row of rawJobMaterials) {
    let skuMap = jobMaterialsByJobId.get(row.jobId);
    if (!skuMap) {
      skuMap = new Map();
      jobMaterialsByJobId.set(row.jobId, skuMap);
    }
    skuMap.set(row.sku, (skuMap.get(row.sku) ?? 0) + row.quantityRequired);
  }

  return {
    shipments: shipmentsResult.status === "fulfilled" ? shipmentsResult.value : [],
    receiving: receivingResult.status === "fulfilled" ? receivingResult.value : [],
    allocations: allocationsResult.status === "fulfilled" ? allocationsResult.value : [],
    products: productsResult.status === "fulfilled" ? productsResult.value : [],
    jobMaterialsByJobId,
  };
}

function buildStockLevelsFromProducts(products: RegistryProduct[]) {
  return Object.fromEntries(
    products
      .filter((product) => {
        const sku = String(product.sku ?? "").trim();
        return sku && product.trackInventory;
      })
      .map((product) => [
        String(product.sku).trim(),
        Number(product.stockQuantity || 0),
      ]),
  );
}

export function WarehouseInventoryProvider({ children }: { children: ReactNode }) {
  const [receivingRecords, setReceivingRecords] = useState<ReceivingRecord[]>([]);
  const [jobAllocations, setJobAllocations] = useState<JobAllocation[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [registryProducts, setRegistryProducts] = useState<RegistryProduct[]>([]);
  const [jobMaterialsByJobId, setJobMaterialsByJobId] = useState<Map<string, Map<string, number>>>(new Map());
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

  /**
   * Phase 3: stateful, event-driven on-hand stock.
   * Mutated only by receiving paths (confirmReceiving / receiveShipment).
   * Unknown SKUs are added on first receipt (starting at 0 + delta) so
   * unmapped inbound is tracked, not silently dropped.
   */
  const [stockLevels, setStockLevels] = useState<Record<string, number>>({});
  const refreshData = useCallback(async () => {
    if (deferHeavyProvider) return;

    const snapshot = await loadWarehouseSnapshot();
    setShipments(snapshot.shipments as Shipment[]);
    setReceivingRecords(snapshot.receiving as ReceivingRecord[]);
    setJobAllocations(snapshot.allocations as JobAllocation[]);
    setRegistryProducts(snapshot.products);
    setStockLevels(buildStockLevelsFromProducts(snapshot.products));
    setJobMaterialsByJobId(snapshot.jobMaterialsByJobId);
  }, [deferHeavyProvider]);

  useEffect(() => {
    if (deferHeavyProvider) return;

    let cancelled = false;
    const hydrate = async () => {
      const snapshot = await loadWarehouseSnapshot();
      if (cancelled) return;
      setShipments(snapshot.shipments as Shipment[]);
      setReceivingRecords(snapshot.receiving as ReceivingRecord[]);
      setJobAllocations(snapshot.allocations as JobAllocation[]);
      setRegistryProducts(snapshot.products);
      setStockLevels(buildStockLevelsFromProducts(snapshot.products));
      setJobMaterialsByJobId(snapshot.jobMaterialsByJobId);
    };
    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [deferHeavyProvider]);

  const stockRecords = useMemo<StockRecord[]>(() => {
    const skuSet = new Set<string>();
    const addSku = (value: unknown) => {
      const sku = String(value ?? "").trim();
      if (sku) skuSet.add(sku);
    };

    Object.keys(stockLevels).forEach(addSku);
    registryProducts
      .filter((product) =>
        String(product.sku ?? "").trim() &&
        (product.trackInventory || Number(product.stockQuantity || 0) !== 0)
      )
      .forEach((product) => addSku(product.sku));
    receivingRecords.forEach((r) => addSku(r.sku));
    jobAllocations.forEach((a) => addSku(a.sku));

    return Array.from(skuSet).map((sku) => {
      const reserved = jobAllocations
        .filter((a) => a.sku === sku && a.status === "Reserved")
        .reduce((s, a) => s + a.quantity, 0);
      const allocated = jobAllocations
        .filter((a) => a.sku === sku && a.status === "Ready for Install")
        .reduce((s, a) => s + a.quantity, 0);
      const incoming = receivingRecords
        .filter((r) => r.sku === sku && r.status !== "Fully Received")
        .reduce((s, r) => s + (r.expectedQuantity - r.receivedQuantity), 0);
      const onHand = stockLevels[sku] ?? 0;
      const product = registryProducts.find((p) => p.sku === sku);
      const productName =
        product?.productName ||
        receivingRecords.find((r) => r.sku === sku)?.productName ||
        jobAllocations.find((a) => a.sku === sku)?.productName ||
        getCatalogProductName(sku) ||
        sku;
      return {
        sku,
        productName: String(productName || sku),
        currentStock: onHand,
        reservedStock: reserved,
        allocatedStock: allocated,
        incomingStock: incoming,
        availableStock: onHand - reserved - allocated,
        reorderThreshold: product?.reorderThreshold ?? 0,
      };
    });
  }, [jobAllocations, receivingRecords, registryProducts, stockLevels]);

  const confirmReceiving = useCallback((recordId: string, quantityReceived: number, discrepancy?: { type: DiscrepancyType; notes: string }) => {
    if (quantityReceived <= 0) return;
    setReceivingRecords((prev) => prev.map((r) => {
      if (r.id !== recordId) return r;
      const newReceived = r.receivedQuantity + quantityReceived;
      const status: ReceivingStatus = newReceived >= r.expectedQuantity ? "Fully Received" : "Partially Received";
      const classification = classifySku(r.sku);
      const unmappedWarning = classification === "unmapped"
        ? `SKU ${r.sku} has no recognized status (canonical / imported / deprecated). Received and tracked, but unmapped.`
        : undefined;
      if (unmappedWarning && typeof console !== "undefined") {
        console.warn(`[Inventory] ${unmappedWarning}`);
      }

      setStockLevels((lvls) => ({
        ...lvls,
        [r.sku]: (lvls[r.sku] ?? 0) + quantityReceived,
      }));

      const updated: ReceivingRecord = {
        ...r, receivedQuantity: newReceived, status,
        receivedAt: new Date().toISOString(), receivedBy: "Warehouse",
        skuClassification: classification,
        ...(unmappedWarning ? { unmappedWarning } : {}),
        ...(discrepancy ? { discrepancy } : {}),
      };

      if (status === "Fully Received") {
        setJobAllocations((prev) => {
          const exists = prev.find((a) => a.sku === r.sku && a.jobId === r.jobId);
          if (exists) return prev;
          return [...prev, {
            id: "alloc-" + Math.random().toString(36).slice(2, 8),
            jobId: r.jobId, jobLabel: r.jobLabel, sku: r.sku,
            productName: r.productName, quantity: newReceived,
            status: "Reserved" as AllocationStatus,
            allocatedAt: new Date().toISOString(), allocatedBy: "Auto",
          }];
        });
      }

      if (r.shipmentId) {
        setShipments((prev) => prev.map((s) => {
          if (s.id !== r.shipmentId) return s;
          const updatedItems = s.items.map((si) =>
            si.sku === r.sku && si.jobId === r.jobId
              ? { ...si, receivedQuantity: newReceived }
              : si
          );
          const allReceived = updatedItems.every((si) => si.receivedQuantity >= si.expectedQuantity);
          return { ...s, items: updatedItems, status: allReceived ? "Received" as ShipmentStatus : s.status };
        }));
      }

      return updated;
    }));
  }, []);

  const allocateToJob = useCallback(async (sku: string, jobId: string, jobLabel: string, quantity: number, productName?: string) => {
    if (quantity <= 0) {
      console.warn(`[Inventory] Allocation skipped: quantity must be > 0 (got ${quantity}).`);
      return null;
    }

    const created = await WarehouseService.createJobAllocation({
      jobId,
      jobLabel,
      sku,
      productName: productName || getCatalogProductName(sku) || sku,
      quantity,
      status: "Reserved",
      allocatedAt: new Date().toISOString(),
      allocatedBy: "Warehouse",
    });

    await refreshData();
    return created;
  }, [refreshData]);


  const markReadyForInstall = useCallback(async (allocationId: string) => {
    const ok = await WarehouseService.updateJobAllocation(allocationId, { status: "Ready for Install" });
    if (ok) {
      setJobAllocations((prev) => prev.map((a) =>
        a.id === allocationId ? { ...a, status: "Ready for Install" as AllocationStatus } : a
      ));
    }
  }, []);

  const markAsInstalled = useCallback(async (allocationId: string) => {
    const allocation = jobAllocations.find((a) => a.id === allocationId);
    const ok = await WarehouseService.updateJobAllocation(allocationId, { status: "Installed" });
    if (ok) {
      setJobAllocations((prev) => prev.map((a) =>
        a.id === allocationId ? { ...a, status: "Installed" as AllocationStatus } : a
      ));
      // Sync quantity_installed on job_materials so the row stays consistent with
      // the allocation state (quantity moves from allocated → installed bucket).
      if (allocation?.jobId && allocation.sku) {
        void syncJobMaterialState(allocation.jobId, allocation.sku, {
          quantityAllocatedDelta: -Math.max(0, allocation.quantity),
          quantityInstalledDelta: Math.max(0, allocation.quantity),
          lifecycleStage: "Installed",
          inventoryStatus: "Installed",
        });
      }
    }
  }, [jobAllocations]);

  const createShipmentsFromPO = useCallback(async (po: {
    poId: string; poNumber: string; vendorId: string; vendorName: string;
    jobId: string; jobLabel: string;
    items: { sku: string; productName: string; quantity: number }[];
  }) => {
    const shipment = await WarehouseService.createShipment({
      poId: po.poId, poNumber: po.poNumber,
      vendorId: po.vendorId, vendorName: po.vendorName,
      items: po.items.map((i) => ({
        sku: i.sku, productName: i.productName,
        expectedQuantity: i.quantity, receivedQuantity: 0,
        jobId: po.jobId, jobLabel: po.jobLabel,
      })),
      status: "Ordered",
      expectedDelivery: new Date(Date.now() + 7 * 86400000).toISOString(),
    });

    await Promise.all(
      po.items.map((item) =>
        WarehouseService.createReceivingRecord({
          poId: po.poId, poNumber: po.poNumber, vendorName: po.vendorName,
          jobId: po.jobId, jobLabel: po.jobLabel,
          sku: item.sku, productName: item.productName,
          expectedQuantity: item.quantity, receivedQuantity: 0,
          status: "Pending" as ReceivingStatus,
          shipmentId: shipment?.id,
          allocationBlocked: false,
          auditEvents: [],
        }),
      ),
    );

    await refreshData();
  }, [refreshData]);

  const updateShipmentStatus = useCallback((shipmentId: string, status: ShipmentStatus) => {
    setShipments((prev) => prev.map((s) =>
      s.id === shipmentId ? {
        ...s, status,
        ...(status === "Delivered" ? { deliveredAt: new Date().toISOString() } : {}),
        ...(status === "Received" ? { receivedAt: new Date().toISOString() } : {}),
      } : s
    ));
  }, []);

  const receiveShipment = useCallback((shipmentId: string, itemReceipts: { sku: string; received: number; discrepancy?: { type: DiscrepancyType; notes: string } }[]) => {
    for (const receipt of itemReceipts) {
      const record = receivingRecords.find((r) => r.shipmentId === shipmentId && r.sku === receipt.sku && r.status !== "Fully Received");
      if (record) {
        confirmReceiving(record.id, receipt.received, receipt.discrepancy);
      }
    }
  }, [receivingRecords, confirmReceiving]);

  const isJobReadyForInstall = useCallback((jobId: string): { ready: boolean; reasons: string[] } => {
    const reasons: string[] = [];
    const jobAllocs = jobAllocations.filter((a) => a.jobId === jobId);

    // Primary check: allocations must exist and all must be "Ready for Install".
    if (jobAllocs.length === 0) {
      return { ready: false, reasons: ["No materials allocated"] };
    }
    const notReady = jobAllocs.filter((a) => a.status !== "Ready for Install");
    if (notReady.length > 0) {
      reasons.push(`${notReady.length} allocation(s) not marked ready for install`);
    }

    // Coverage check: all required SKUs must be covered by "Ready for Install" allocations.
    const requiredSkus = jobMaterialsByJobId.get(jobId);
    if (requiredSkus && requiredSkus.size > 0) {
      const readyAllocatedBySku = new Map<string, number>();
      for (const a of jobAllocs) {
        if (a.status === "Ready for Install") {
          readyAllocatedBySku.set(a.sku, (readyAllocatedBySku.get(a.sku) ?? 0) + a.quantity);
        }
      }
      let uncoveredCount = 0;
      for (const [sku, required] of requiredSkus.entries()) {
        if ((readyAllocatedBySku.get(sku) ?? 0) < required) {
          uncoveredCount++;
        }
      }
      if (uncoveredCount > 0) {
        reasons.push(`${uncoveredCount} material(s) not fully allocated as ready`);
      }
    }

    // Secondary: flag receiving discrepancies as a warning (non-blocking if allocations are ready).
    const jobReceiving = receivingRecords.filter((r) => r.jobId === jobId);
    const withDisc = jobReceiving.filter((r) => r.discrepancy);
    if (withDisc.length > 0) {
      reasons.push(`${withDisc.length} item(s) with receiving discrepancies`);
    }

    return { ready: reasons.length === 0, reasons };
  }, [jobAllocations, jobMaterialsByJobId, receivingRecords]);

  const getStockBySku = useCallback((sku: string) => stockRecords.find((s) => s.sku === sku), [stockRecords]);
  const getPendingReceiving = useCallback(() => receivingRecords.filter((r) => r.status !== "Fully Received"), [receivingRecords]);
  const getAllocationsByJob = useCallback((jobId: string) => jobAllocations.filter((a) => a.jobId === jobId), [jobAllocations]);
  const lowStockItems = useMemo(() => stockRecords.filter((s) => s.reorderThreshold > 0 && s.availableStock <= s.reorderThreshold), [stockRecords]);

  return (
    <WarehouseInventoryContext.Provider value={{
      stockRecords, receivingRecords, jobAllocations, shipments,
      setReceivingRecords, setJobAllocations, setShipments,
      confirmReceiving, allocateToJob, markReadyForInstall, markAsInstalled,
      getStockBySku, getPendingReceiving, getAllocationsByJob, lowStockItems,
      createShipmentsFromPO, updateShipmentStatus, receiveShipment, isJobReadyForInstall,
      refreshData,
    }}>
      {children}
    </WarehouseInventoryContext.Provider>
  );
}

export function useWarehouseInventory() {
  const ctx = useContext(WarehouseInventoryContext);
  if (!ctx) throw new Error("useWarehouseInventory must be used within WarehouseInventoryProvider");
  return ctx;
}
